"use client";
import {
  Card,
  CardProps,
  Col,
  Row,
  theme,
  Image as AntImage,
  Alert,
} from "antd";
import Text from "../../../common/Typography/Text";
import FlightDownIcon from "../../../icons/FlightDownIcon";
import DestinationIcon from "../../../icons/DestinationIcon";
import { useAppSelector } from "@/lib/redux/hooks";
import {
  FareRuleItem,
  TypeAirport,
  TypeBaggage,
  TypeFlight,
  TypeInventory,
  TypeSegment,
} from "@/lib/redux/features/flight/type";
import { FC, Fragment, ReactNode } from "react";
import { getCabinClassCode } from "@/utils/flight/getCabinClassCode";
import CarryIcon from "../../../icons/CarryIcon";
import BaggageIcon from "../../../icons/BaggageIcon";
import RejectIcon from "../../../icons/RejectIcon";
import { getDurationSegments } from "@/utils/flight/getDurationSegment";
import { getDatetimeSegment } from "@/utils/flight/getDatetimeSegment";
import { useAirlines } from "@/hooks/flight/useAirlines";
import { useAirports } from "@/hooks/flight/useAirports";
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import { useIsRoundAway } from "@/hooks/flight/useIsRoundAway";
import { useTETranslation } from "@/hooks/app/useTETranslation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPassport } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useAllFareRules } from "@/hooks/flight/useAllFareRules";
import {
  formatBaggageLabel,
  getBaggageConditions,
} from "@/utils/flight/getBaggageConditions";
import { getBaggageDisplayLabel } from "@/utils/flight/getBaggageDisplayLabel";
import { isGalileoSystem } from "@/utils/flight/isGalileoSystem";
import {
  getSegmentIntermediateStops,
  parseStopDuration,
} from "@/utils/flight/getSegmentIntermediateStops";
import { getSegmentAmenities } from "@/utils/flight/getSegmentAmenities";
import { getLegCabinClass } from "@/utils/flight/getLegCabinClass";
import FlightAmenitiesGrid from "../FlightAmenitiesGrid";
import { airlineLargeLogo as defaultAirlineLargeLogo } from "@/constants/flight/airline";

type FlightDetailSegmentsProps = CardProps & {
  flight?: TypeFlight;
  shownDuration?: boolean;
  shownDetail?: boolean;
  shownAirline?: boolean;
  group?: boolean; // apply for 1G
  isDomestic: boolean;
  baggages?: TypeBaggage[];
  inventory?: TypeInventory;
};

const FlightDetailSegments: FC<FlightDetailSegmentsProps> = ({
  inventory,
  baggages: _baggages,
  flight,
  isDomestic,
  shownAirline: _shownAirline,
  shownDuration: _shownDuration,
  shownDetail,
  group = true,
  ...props
}) => {
  const {
    getTranslatedText,
    getAirportCityName,
    getAirportName,
    getCabinClassName,
  } = useTETranslation();
  const token = theme.useToken().token;
  const airlineLargeLogo = useAirlines();
  const isDesktop = useIsDesktop();
  const isRoundAway = useIsRoundAway();
  const airports = useAirports();
  const { fareRules0, fareRules1 } = useAllFareRules();
  const fareRules = isDomestic ? fareRules1 : fareRules0;

  const selectedFlight = useAppSelector((state) => {
    const selectedFlightId = state.flight.selectedFlightId;
    if (!selectedFlightId) {
      return null;
    }
    return state.flight.flightsResult.flights[selectedFlightId];
  });
  const { t } = useTranslation();

  const _flight = flight || selectedFlight;
  const fareData = useAppSelector(
    (state) =>
      state.flight.flightsResult.fares[_flight?.fareDataId || 0] ||
      (_flight as any)?.fareData,
  );
  const is1G = isGalileoSystem(fareData?.system);
  const flightIds = useAppSelector(
    (state) =>
      state.flight.flightsResult.layout.mappingFareFlights[
        _flight?.fareDataId || 0
      ] || [],
  );
  const flights = useAppSelector((state) =>
    flightIds.map((flightId) => state.flight.flightsResult.flights[flightId]),
  );

  const baggages = _baggages || [];

  const renderRule = ({
    label,
    value,
    icon,
    strong,
  }: {
    strong?: boolean;
    icon: ReactNode;
    label: string;
    value: any;
  }) => {
    return (
      <Row
        wrap={false}
        className="flights__list--item-classitem-rule"
        align="top"
        gutter={[10, 10]}
      >
        <Col>{icon}</Col>
        <Col>
          <Row align="middle" gutter={[10, 10]}>
            <Col>
              <Text type="secondary" style={{ fontSize: "1.4rem" }}>
                {label ? `${label}: ` : null}
                <Text strong={strong}>{value}</Text>
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  let shownAirline = _shownAirline,
    shownDuration = _shownDuration;
  if (typeof _shownAirline === "undefined") {
    shownAirline = true;
  }

  if (typeof _shownDuration === "undefined") {
    shownDuration = true;
  }

  return (
    <Card
      size="small"
      variant="borderless"
      {...props}
      style={{ borderRadius: 0, ...props.style }}
    >
      <Row justify="center" gutter={[30, 30]} wrap={false}>
        <Col span={24}>
          {(is1G && !isDesktop && group ? flights : [_flight]).map(
            (flightData, idx) => {
              const segments: (TypeSegment & {
                fromAirport: TypeAirport;
                toAirport: TypeAirport;
                fareRules: FareRuleItem;
                selectedCabinClass?: string;
              })[] = flightData
                ? (flightData.segments.map((segment: any, segmentIndex: number) => {
                    const resolvedInventory: TypeInventory | undefined =
                      inventory ||
                      (flight as any)?.inventory ||
                      flightData?.inventories?.[0] ||
                      _flight?.inventories?.[0];
                    const baggageConditions = getBaggageConditions(
                      flightData,
                      segment,
                      resolvedInventory,
                    );
                    const segmentRoute = `${segment.startPoint || ""}${segment.endPoint || ""}`
                      .replace(/[^a-z0-9]/gi, "")
                      .toUpperCase();
                    const firstSegment = flightData.segments[0];
                    const lastSegment = flightData.segments[flightData.segments.length - 1];
                    const itineraryRoute = `${firstSegment?.startPoint || ""}${lastSegment?.endPoint || ""}`
                      .replace(/[^a-z0-9]/gi, "")
                      .toUpperCase();
                    const segmentBaggages = baggages.filter((baggage) => {
                      const route = (
                        baggage.route ||
                        `${baggage.startPoint || ""}${baggage.endPoint || ""}`
                      )
                        .replace(/[^a-z0-9]/gi, "")
                        .toUpperCase();
                      return route === segmentRoute || (segmentIndex === 0 && route === itineraryRoute);
                    });
                    const baggagePackageCounts = segmentBaggages.reduce(
                      (counts, baggage) => {
                        const label = getBaggageDisplayLabel(baggage);
                        counts.set(label, (counts.get(label) || 0) + 1);
                        return counts;
                      },
                      new Map<string, number>(),
                    );
                    const addOnBaggageText = Array.from(baggagePackageCounts)
                      .map(([label, count]) => `${count > 1 ? `${count} x ` : ""}${label}`)
                      .join(", ");
                    const pieceLabel = t("common.piece");
                    const baggageRuleItems = [
                      ...(baggageConditions.handBaggageData
                        ? [{
                            rulesTitle: t("flight.hand_baggage"),
                            rulesText: formatBaggageLabel(
                              {
                                count: baggageConditions.handBaggageCount,
                                weight: baggageConditions.handBaggage,
                                rawText: baggageConditions.handBaggageRaw,
                              },
                              pieceLabel,
                            ),
                          }]
                        : []),
                      ...(baggageConditions.allowanceBaggageData
                        ? [{
                            rulesTitle: t("flight.allowanced_baggage"),
                            rulesText: formatBaggageLabel(
                              {
                                count: baggageConditions.allowanceBaggageCount,
                                weight: baggageConditions.allowanceBaggage,
                                rawText: baggageConditions.allowanceBaggageRaw,
                              },
                              pieceLabel,
                            ),
                          }]
                        : []),
                    ];
                    const selectedFareRules = is1G
                      ? {
                          route: null,
                          ruleNumber: "",
                          listRuleGroups: [
                            {
                              categoryId: "1",
                              rulesCategory: t("flight.baggage_conditions"),
                              subRuleCategory: [
                                {
                                  rulesTitle: t("flight.hand_baggage"),
                                  rulesText: formatBaggageLabel(
                                    {
                                      count: baggageConditions.handBaggageCount,
                                      weight: baggageConditions.handBaggage,
                                      rawText: baggageConditions.handBaggageRaw,
                                    },
                                    pieceLabel,
                                  ),
                                },
                                {
                                  rulesTitle: t("flight.allowanced_baggage"),
                                  rulesText: formatBaggageLabel(
                                    {
                                      count: baggageConditions.allowanceBaggageCount,
                                      weight: baggageConditions.allowanceBaggage,
                                      rawText: baggageConditions.allowanceBaggageRaw,
                                    },
                                    pieceLabel,
                                  ),
                                },
                              ],
                            },
                          ],
                        }
                      : resolvedInventory
                        ? (fareRules[segment.airline]?.listFareRules || {})[
                            getCabinClassCode(
                              segment.airline.replace("_", ""),
                              resolvedInventory?.cabinClass,
                            )
                          ]
                        : (fareRules[segment.airline]?.listFareRules || {})[
                            getCabinClassCode(
                              segment.airline.replace("_", ""),
                              segment?.cabinEn,
                            )
                          ];
                    const ruleGroups = selectedFareRules?.listRuleGroups || [];
                    const hasBaggageRuleGroup = ruleGroups.some(
                      (ruleGroup) => ruleGroup.categoryId === "1",
                    );
                    const fareRulesForSegment =
                      !hasBaggageRuleGroup &&
                      (baggageRuleItems.length > 0 || segmentBaggages.length > 0)
                        ? {
                            route: selectedFareRules?.route ?? null,
                            ruleNumber: selectedFareRules?.ruleNumber ?? "",
                            listRuleGroups: [
                              {
                                categoryId: "1",
                                rulesCategory: t("flight.baggage_conditions"),
                                subRuleCategory: baggageRuleItems,
                              },
                              ...ruleGroups,
                            ],
                          }
                        : selectedFareRules;

                    return {
                      ...segment,
                      segmentBaggages,
                      addOnBaggageText,
                      selectedCabinClass:
                        getLegCabinClass(segment.flightNumber, _flight?.fareDataId, resolvedInventory, flights) ||
                        segment.cabinEn ||
                        resolvedInventory?.cabinClass ||
                        resolvedInventory?.cabinClassType ||
                        segment.cabinClassType ||
                        segment.cabinClass,
                      fromAirport: airports[segment.startPoint],
                      toAirport: airports[segment.endPoint],
                      fareRules: fareRulesForSegment,
                    };
                  }) as any[])
                : [];

              return (
                <Fragment key={flightData?.id || idx}>
                  {isGalileoSystem(fareData?.system) &&
                    group &&
                    isRoundAway &&
                    !isDesktop && (
                      <Row
                        className="passenger__order--flight-top"
                        style={idx === 0 ? {} : { marginTop: 20 }}
                      >
                        <Col className="passenger__order--flight-top--box">
                          <Text strong className="te-text-small">
                            {flightData?.leg === 1
                              ? t("flight.return_way")
                              : t("flight.departure_way")}
                          </Text>
                        </Col>
                      </Row>
                    )}
                  {segments.map((segment, index) => {
                    const segmentBaggages = (segment as any).segmentBaggages as TypeBaggage[];
                    const addOnBaggageText = (segment as any).addOnBaggageText as string;
                    const cabinClass = String(
                      segment.selectedCabinClass || "",
                    ).toLowerCase();
                    const cabinLabelKey = cabinClass.includes("business")
                      ? "flight.business"
                      : cabinClass.includes("economy")
                        ? "flight.economy"
                        : undefined;
                    const hasDetailedCabinLabel =
                      !!segment.selectedCabinClass &&
                      !["economy", "business", "first class"].includes(
                        cabinClass,
                      );
                    const selectedCabinClass =
                      segment.selectedCabinClass || "";
                    const marketingAirlineCode = String(
                      segment.airline || segment.operatingAirline || "",
                    ).replace("_", "");
                    const operatingAirlineCode = String(
                      segment.operatingAirline || "",
                    ).replace("_", "");
                    const marketingAirlineLogo =
                      airlineLargeLogo[marketingAirlineCode] ||
                      defaultAirlineLargeLogo[marketingAirlineCode] ||
                      airlineLargeLogo[operatingAirlineCode] ||
                      defaultAirlineLargeLogo[operatingAirlineCode] ||
                      {};
                    const operatingAirlineLogo =
                      airlineLargeLogo[operatingAirlineCode] ||
                      defaultAirlineLargeLogo[operatingAirlineCode] ||
                      {};
                    const isOperatedByOtherAirline =
                      !!operatingAirlineCode &&
                      operatingAirlineCode !== marketingAirlineCode;
                    const cabinLabel = hasDetailedCabinLabel
                      ? getCabinClassName(
                          String(
                            segment.airline || segment.operatingAirline || "",
                          ),
                          selectedCabinClass,
                        ) || selectedCabinClass
                      : "";
                    const { hours, mins, startTime, endTime } =
                      getDurationSegments([segment, segment], fareData);
                    const listFareRules = segment?.fareRules;
                    const isFirstSegment = index === 0;
                    const isLastSegment = index === segments.length - 1;
                    const prevSeg = segments[index - 1];
                    const reportedStopTime = Number(prevSeg?.stopTime);
                    const transitMinutes = prevSeg
                      ? reportedStopTime > 0
                        ? reportedStopTime
                        : Math.max(
                            0,
                            Math.round(
                              getDatetimeSegment(
                                segment.startTime,
                                segment.startTimeZoneOffset,
                              ).diff(
                                getDatetimeSegment(
                                  prevSeg.endTime,
                                  prevSeg.endTimeZoneOffset,
                                ),
                                "minute",
                                true,
                              ),
                            ),
                          )
                      : 0;
                    const transitHour = Math.floor(transitMinutes / 60);
                    const transitMins = transitMinutes % 60;
                    // Transit ẩn (chỉ GALILEO) - hiển thị điểm tạm dừng kỹ thuật trong 1 chặng bay
                    const intermediateStops = is1G
                      ? getSegmentIntermediateStops(flightData, segment)
                      : [];
                    // Tiện ích chuyến bay (chỉ GALILEO) - suất ăn, WiFi, giải trí, ghế... theo từng chặng
                    const amenities = is1G
                      ? getSegmentAmenities(flightData, index)
                      : [];

                    return (
                      <Fragment key={segment.startPoint}>
                        {transitMinutes > 0 && (
                          <Row
                            style={{ marginBlock: "2rem", maxWidth: "40rem" }}
                          >
                            <Alert
                              style={{ padding: "0.5rem 1rem" }}
                              title={
                                <Text style={{ fontSize: "1.2rem" }}>
                                  {t("flight.transit_at")}{" "}
                                  <Text strong>
                                    {getTranslatedText(
                                      segments[index - 1]?.toAirport?.cityVi,
                                      segments[index - 1]?.toAirport?.cityEn,
                                    )}{" "}
                                    {transitHour}h{transitMins}'
                                  </Text>
                                </Text>
                              }
                              description={
                                <Row>
                                  <Col>
                                    <Row>
                                      <Text style={{ fontSize: "1.2rem" }}>
                                        <FontAwesomeIcon icon={faPassport} />{" "}
                                        {t("flight.transit_at_desc")}
                                      </Text>
                                    </Row>
                                    <Row>
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: "1.2rem" }}
                                      >
                                        {t("flight.transit_at_note")}
                                      </Text>
                                    </Row>
                                  </Col>
                                </Row>
                              }
                            />
                          </Row>
                        )}
                        <Row wrap={false} style={{ marginBottom: "1rem" }}>
                          <Col style={{ width: "10rem", minWidth: "10rem" }}>
                            <Row
                              justify="space-between"
                              align="middle"
                              style={{ flexDirection: "row", height: "100%" }}
                            >
                              <Col
                                span={24}
                                style={{
                                  marginBottom: "2rem",
                                  height: "fit-content",
                                  alignSelf: "start",
                                }}
                              >
                                <Row style={{ width: "fit-content" }}>
                                  <Text strong>
                                    {startTime.format("HH:mm")}
                                  </Text>
                                </Row>
                                <Row style={{ width: "fit-content" }}>
                                  <Text type="secondary">
                                    {startTime.format("DD/MM/YYYY")}
                                  </Text>
                                </Row>
                              </Col>
                              {shownDuration && (
                                <Col
                                  flex={1}
                                  style={{
                                    marginBottom: "2rem",
                                    height: "4rem",
                                  }}
                                >
                                  <Text type="secondary">
                                    {`${hours}h${mins}`}
                                  </Text>
                                </Col>
                              )}
                              <Col
                                span={24}
                                style={{
                                  height: "fit-content",
                                  alignSelf: "end",
                                }}
                              >
                                <Row style={{ width: "fit-content" }}>
                                  <Text strong>{endTime.format("HH:mm")}</Text>
                                </Row>
                                <Row style={{ width: "fit-content" }}>
                                  <Text type="secondary">
                                    {endTime.format("DD/MM/YYYY")}
                                  </Text>
                                </Row>
                              </Col>
                            </Row>
                          </Col>
                          <Col style={{ marginInline: "1rem" }}>
                            <Row
                              justify="center"
                              align="middle"
                              style={{
                                height: "100%",
                                flexDirection: "column",
                                marginTop: "1rem",
                              }}
                            >
                              <Col flex={1}>
                                <Row justify="center">
                                  <FlightDownIcon
                                    style={{
                                      fontSize: "2rem",
                                      color: token.colorPrimary,
                                    }}
                                  />
                                </Row>
                                <Row
                                  justify="center"
                                  style={{
                                    height: `calc(100% - ${isLastSegment || isFirstSegment ? (segments.length === 1 ? 6 : 5) : 5}rem)`,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 2,
                                      backgroundColor: token.colorPrimary,
                                      height: "100%",
                                    }}
                                  />
                                </Row>
                                {isLastSegment && (
                                  <Row justify="center">
                                    <DestinationIcon
                                      style={{
                                        fontSize: "2rem",
                                        color: token.colorPrimary,
                                      }}
                                    />
                                  </Row>
                                )}
                                {!isLastSegment && (
                                  <Row justify="center">
                                    <div
                                      style={{
                                        width: "1rem",
                                        height: "1rem",
                                        borderRadius: "50%",
                                        border: `3px solid ${token.colorPrimary}`,
                                      }}
                                    />
                                  </Row>
                                )}
                              </Col>
                            </Row>
                          </Col>
                          <Col>
                            <Row>
                              <Col span={24} style={{ marginBottom: "2rem" }}>
                                <Row style={{ width: "fit-content" }}>
                                  <Text strong>
                                    {getAirportCityName(segment?.fromAirport)} (
                                    {segment?.fromAirport?.code})
                                    {segment.startTerminal ? (
                                      ` T${segment.startTerminal}`
                                    ) : (
                                      <></>
                                    )}
                                  </Text>
                                </Row>
                                <Row style={{ width: "fit-content" }}>
                                  <Text type="secondary">
                                    {getAirportName(segment?.fromAirport, {
                                      shownCode: false,
                                    })}
                                  </Text>
                                </Row>
                              </Col>
                            </Row>
                            {shownAirline && (
                              <Row
                                align="middle"
                                gutter={[10, 10]}
                                style={{ marginBottom: "2rem" }}
                              >
                                <Col>
                                  <AntImage
                                    preview={false}
                                    src={marketingAirlineLogo.url}
                                    alt={marketingAirlineLogo.name}
                                    style={{
                                      width: 66,
                                      height: 27,
                                      objectFit: "contain",
                                    }}
                                  />
                                </Col>
                                <Col>
                                  <Row>
                                    <Text strong className="te-text-small">
                                      {marketingAirlineLogo.name || marketingAirlineCode}
                                    </Text>
                                  </Row>
                                  {isOperatedByOtherAirline && (
                                    <Row>
                                      <Text type="secondary" className="te-text-small">
                                        {t("flight.operated_by", {
                                          airline:
                                            operatingAirlineLogo.name || operatingAirlineCode,
                                        })}
                                      </Text>
                                    </Row>
                                  )}
                                  <Row>
                                    <Text
                                      strong
                                      style={{ color: token.colorPrimary }}
                                      className="te-text-small"
                                    >
                                      {segment.flightNumber}
                                    </Text>
                                    {cabinLabelKey && (
                                      <span className="flights__detail-leg-flightno-row">
                                        <span
                                          className="flights__detail-leg-cabin-separator"
                                          aria-hidden="true"
                                        >
                                          •
                                        </span>
                                        <Text
                                          strong
                                          className="flights__detail-leg-cabin"
                                        >
                                          {cabinLabel ||
                                            t(cabinLabelKey, {
                                              defaultValue:
                                                cabinClass.includes("business")
                                                  ? "Thương gia"
                                                  : "Phổ thông",
                                            })}
                                        </Text>
                                      </span>
                                    )}
                                  </Row>
                                </Col>
                              </Row>
                            )}
                            {shownDetail && amenities.length > 0 && (
                              <FlightAmenitiesGrid items={amenities} />
                            )}
                            {shownDetail &&
                              (listFareRules?.listRuleGroups || []).length >
                                0 && (
                                <Row style={{ marginBlock: "2rem" }}>
                                  <Col span={24}>
                                    {(listFareRules?.listRuleGroups || []).map(
                                      (ruleGroup) => {
                                        return (
                                          <Fragment
                                            key={
                                              ruleGroup.categoryId ||
                                              ruleGroup.rulesCategory
                                            }
                                          >
                                            {ruleGroup.subRuleCategory
                                              .concat(
                                                ruleGroup.categoryId === "1" &&
                                                  segmentBaggages.length > 0
                                                  ? [
                                                      {
                                                        rulesTitle: t(
                                                          "flight.add_on_baggage",
                                                        ),
                                                            rulesText: addOnBaggageText,
                                                      },
                                                    ]
                                                  : [],
                                              )
                                              .map((fareRuleItem, index) => {
                                                const itemIcon = (
                                                  fareRuleItem as any
                                                ).icon;
                                                return (
                                                  <Fragment
                                                    key={`${ruleGroup.categoryId}-${fareRuleItem.rulesTitle || index}`}
                                                  >
                                                    {renderRule({
                                                      strong:
                                                        ruleGroup.categoryId ===
                                                        "1",
                                                      icon:
                                                        itemIcon ===
                                                        "baggage" ? (
                                                          <BaggageIcon />
                                                        ) : itemIcon ===
                                                          "carry" ? (
                                                          <CarryIcon />
                                                        ) : ruleGroup.categoryId ===
                                                          "1" ? (
                                                          index === 0 ? (
                                                            <CarryIcon />
                                                          ) : (
                                                            <BaggageIcon />
                                                          )
                                                        ) : (
                                                          <RejectIcon />
                                                        ),
                                                      label:
                                                        fareRuleItem.rulesTitle,
                                                      value:
                                                        fareRuleItem.rulesText,
                                                    })}
                                                  </Fragment>
                                                );
                                              })}
                                          </Fragment>
                                        );
                                      },
                                    )}
                                  </Col>
                                </Row>
                              )}
                            {intermediateStops.map((stop, stopIndex) => {
                              const stopAirport = airports[stop.value];
                              const { hours: stopHour, mins: stopMin } =
                                parseStopDuration(stop.duration);
                              return (
                                <Row
                                  key={`stop-${stop.value}-${stopIndex}`}
                                  style={{
                                    marginBlock: "2rem",
                                    maxWidth: "40rem",
                                  }}
                                >
                                  <Alert
                                    type="warning"
                                    style={{
                                      padding: "0.5rem 1rem",
                                      width: "100%",
                                    }}
                                    title={
                                      <Text style={{ fontSize: "1.2rem" }}>
                                        {t("flight.stopover_at")}{" "}
                                        <Text strong>
                                          {getTranslatedText(
                                            stopAirport?.cityVi,
                                            stopAirport?.cityEn,
                                          ) || stop.value}
                                        </Text>{" "}
                                        <Text strong>
                                          {stopHour}h {stopMin}m
                                        </Text>
                                      </Text>
                                    }
                                    description={
                                      <Row>
                                        <Col>
                                          <Row>
                                            <Text
                                              style={{ fontSize: "1.2rem" }}
                                            >
                                              <FontAwesomeIcon
                                                icon={faPassport}
                                              />{" "}
                                              {t("flight.transit_at_desc")}
                                            </Text>
                                          </Row>
                                          <Row>
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: "1.2rem" }}
                                            >
                                              {t("flight.transit_at_note")}
                                            </Text>
                                          </Row>
                                        </Col>
                                      </Row>
                                    }
                                  />
                                </Row>
                              );
                            })}
                            <Row align="bottom">
                              <Col span={24}>
                                <Row style={{ width: "fit-content" }}>
                                  <Text strong>
                                    {getAirportCityName(segment?.toAirport)} (
                                    {segment?.toAirport?.code})
                                    {segment.endTerminal ? (
                                      ` T${segment.endTerminal}`
                                    ) : (
                                      <></>
                                    )}
                                  </Text>
                                </Row>
                                <Row style={{ width: "fit-content" }}>
                                  <Text type="secondary">
                                    {getAirportName(segment?.toAirport, {
                                      shownCode: false,
                                    })}
                                  </Text>
                                </Row>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            },
          )}
        </Col>
      </Row>
    </Card>
  );
};

FlightDetailSegments.displayName = "FlightDetailSegments";

export default FlightDetailSegments;
